/**
 * Admin Stats — fetches the 4 system-wide counters that the admin
 * dashboard at `/admin/index.html` shows under "סטטיסטיקות מערכת":
 *   - 🚗 רכבים פעילים   (plateIndex — currently-bound plates only)
 *   - 👥 משתמשים פעילים (phoneIndex — currently-bound phones only)
 *   - 💬 הודעות         (chatMessages, iterated per plate)
 *   - 📞 שיחות          (calls)
 *
 * Vehicle/user counts come from `plateIndex` / `phoneIndex` rather than
 * `numChildren()` over `/vehicles` and `/users`. The indexes have exactly
 * one entry per currently-bound plate or phone — they're updated atomically
 * by register/replace/orphan-cleanup. A raw count over `/users` would also
 * include admin stubs, anonymous-auth residuals, and uid duplicates from
 * re-registrations, all of which inflate the number.
 *
 * Queries mirror those in `www/admin/index.html::loadStatistics()` so the
 * banner shows the EXACT same numbers as the admin dashboard.
 *
 * Costs: 4 RTDB reads (`once('value')`). For a small DB (~hundreds of
 * vehicles / users) this is fast. We cache the result for 30 seconds so
 * the admin banner can refresh every 60s without spamming RTDB.
 */

import { ref, auth } from '../../core/firebase.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('admin-stats');

/**
 * The 4 stats reads (`/users`, `/chatMessages`, `/calls`) require
 * `root.child('adminUsers').child(auth.uid).exists()` in the RTDB rules.
 * The new admin gating uses `/config/admins/phones/{phone}` for UI, but
 * those legacy rules still apply at read time. Mirror the dashboard's
 * `ensureAdminSession()` — register self in `/adminUsers/{uid}` before
 * each fetch so the reads pass even for anonymous Firebase sessions.
 */
async function ensureAdminRegistered() {
  const uid = auth().currentUser?.uid;
  if (!uid) return;
  try {
    await ref('adminUsers/' + uid).set(true);
  } catch (e) {
    log.warn('adminUsers self-register failed:', e?.message ?? e);
  }
}

/**
 * @typedef {Object} AdminStats
 * @property {number} vehicles
 * @property {number} users
 * @property {number} messages
 * @property {number} calls
 * @property {number} fetchedAt  ms timestamp (for cache TTL)
 */

/** Cache lifetime — same value the home-feed polling uses (30s). */
const CACHE_TTL_MS = 30 * 1000;

/** @type {AdminStats | null} */
let cached = null;
/** @type {Promise<AdminStats> | null} */
let inFlight = null;

/**
 * Fetch the 4 system counters. Returns a cached result if it's still
 * fresh (≤30s old) unless `force=true`.
 *
 * @param {boolean} [force]  bypass cache
 * @returns {Promise<AdminStats>}
 */
export async function getStats(force) {
  if (!force && cached && (Date.now() - cached.fetchedAt) < CACHE_TTL_MS) {
    return cached;
  }
  // De-duplicate concurrent callers — only one network roundtrip at a time.
  if (inFlight) return inFlight;

  inFlight = doFetch().finally(() => { inFlight = null; });
  return inFlight;
}

async function doFetch() {
  // Establish read permission for the admin-gated paths (`chatMessages`,
  // `calls`). The two index paths are readable by any auth'd user so they
  // don't need the self-register; we still await it for the other two.
  await ensureAdminRegistered();

  // Promise.allSettled — one failed read shouldn't zero out all four.
  // `plateIndex` / `phoneIndex` have lax `.read: "auth != null"` rules and
  // almost always succeed; `chatMessages` / `calls` depend on the
  // self-register above.
  const [plateIdxRes, phoneIdxRes, messagesRes, callsRes] = await Promise.allSettled([
    ref('plateIndex').once('value'),
    ref('phoneIndex').once('value'),
    ref('chatMessages').once('value'),
    ref('calls').once('value'),
  ]);

  const vehicles = countTop(plateIdxRes, 'plateIndex');
  const users    = countTop(phoneIdxRes, 'phoneIndex');
  const messages = countNested(messagesRes, 'chatMessages');
  const calls    = countTop(callsRes,    'calls');

  cached = { vehicles, users, messages, calls, fetchedAt: Date.now() };
  log.info('refreshed:', cached);
  return cached;
}

/**
 * Extract `numChildren()` from a settled snapshot promise, or log + return 0
 * on rejection. Keeps `doFetch` readable.
 * @param {PromiseSettledResult<any>} res
 * @param {string} label
 * @returns {number}
 */
function countTop(res, label) {
  if (res.status === 'fulfilled') {
    return res.value.numChildren() || 0;
  }
  log.warn(`${label} read failed:`, res.reason?.message ?? res.reason);
  return 0;
}

/**
 * Sum child counts across a 2-level tree (chatMessages/{plate}/{msgId}).
 * @param {PromiseSettledResult<any>} res
 * @param {string} label
 * @returns {number}
 */
function countNested(res, label) {
  if (res.status !== 'fulfilled') {
    log.warn(`${label} read failed:`, res.reason?.message ?? res.reason);
    return 0;
  }
  let total = 0;
  res.value.forEach((/** @type {any} */ plateSnap) => {
    total += plateSnap.numChildren() || 0;
  });
  return total;
}

/** Force a refresh on next read (used by the banner's manual refresh button). */
export function invalidateCache() {
  cached = null;
}
